import App from '@/App'
import { config } from '@/configs'
import { setupBlobStorageCallLogging } from '@/domains/appShell/helpers/blobStorageCallLogging'
import {
  DEV_MOCKS_START_TIMEOUT_MS,
  renderWhenDevMocksReady,
} from '@/domains/testing/devMocks/bootDevMocks'
import { devMocksStatusAtom } from '@/domains/testing/devMocks/devMocks.store'
import { featureFlagsAtom } from '@/domains/testing/featureToggle/featureFlags.store'

import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { ToastContainer } from 'react-toastify'
import { Tooltip } from 'react-tooltip'

import Hotjar from '@hotjar/browser'
import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import * as Sentry from '@sentry/react'
import { getDefaultStore } from 'jotai'

import '@/services/i18n.service'

Sentry.init(config.sentry)

if (config.hotjar) {
  Hotjar.init(config.hotjar.hotjarId, config.hotjar.hotjarVersion ?? 6)
}

// Initialize Application Insights
if (config.applicationInsights) {
  const appInsights = new ApplicationInsights({
    config: config.applicationInsights,
  })
  appInsights.loadAppInsights()
  appInsights.trackPageView()

  // TODO temp: remove once the migration to secured/proxied file access is confirmed complete
  // (see @/shared/helpers/protectedFile) and the monitoring period has elapsed.
  setupBlobStorageCallLogging()
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    // TODO use logger
    // eslint-disable-next-line no-console
    console.warn('Uncaught error', error, errorInfo.componentStack)
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
})

const renderApp = () =>
  root.render(
    <StrictMode>
      <App />
      <ToastContainer hideProgressBar theme="colored" />
      <Tooltip id="tooltip" className="tooltip dark" disableStyleInjection="core" />
    </StrictMode>,
  )

// Mocked data for the TVM → BO pages (card #4314): only in `testingTooling` environments, only
// with the `referentialMocks` flag on, and never longer than the timeout. Everywhere else (UAT,
// PROD, DEV without the flag) the app renders at once, exactly as before.
renderWhenDevMocksReady({
  testingTooling: config.testingTooling,
  flags: getDefaultStore().get(featureFlagsAtom),
  load: async signal => {
    const { enableDevMocks } = await import('@/domains/testing/devMocks/enableDevMocks')
    await enableDevMocks(signal)
  },
  timeoutMs: DEV_MOCKS_START_TIMEOUT_MS,
  onFailure: reason => {
    // eslint-disable-next-line no-console
    console.warn('[dev mocks] not started, the app runs without them', reason)
    getDefaultStore().set(devMocksStatusAtom, 'failed')
  },
  render: renderApp,
})
